import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { getDvrTime } from 'support/mediaTileHelper';
import { parseDate } from 'common/support/dateHelper';
import useInterval from 'hooks/interval';

import dvrBackgroundSrc from 'assets/images/tile/dvr_bg.png';

import MediaTile from './MediaTile';

import styles from './DvrTile.module.scss';
import { TileSize } from 'app-types';

const messages = {
  d: {
    id: 'tile.dvrTime.dayAgo',
    description: 'dvr time',
  },
  dLong: {
    id: 'tile.dvrTime.dayAgoLong',
    description: 'dvr time',
  },
  h: {
    id: 'tile.dvrTime.hourAgo',
    description: 'dvr time',
  },
  hLong: {
    id: 'tile.dvrTime.hourAgoLong',
    description: 'dvr time',
  },
  m: {
    id: 'tile.dvrTime.minuteAgo',
    description: 'dvr time',
  },
  mLong: {
    id: 'tile.dvrTime.minuteAgoLong',
    description: 'dvr time',
  },
  s: {
    id: 'tile.dvrTime.secondAgo',
    description: 'dvr time',
  },
  sLong: {
    id: 'tile.dvrTime.secondAgoLong',
    description: 'dvr time',
  },
  nowPlaying: {
    id: 'tile.nowPlaying',
    description: 'Text for now playing video flag',
  },
};

interface TimeString {
  display?: string;
  tts?: string;
}

export interface DvrTileProps {
  title?: string | null;
  timeSeconds?: number | null;
  src?: string | null;
  size?: TileSize | null;
  isNowPlaying?: boolean;
  focusId?: string | number | null;
  className?: string | null;
  onClick?: () => void;
  onFocusSet?: () => void;
}

const DvrTile = ({
  title = null,
  timeSeconds = null,
  src = null,
  size = null,
  isNowPlaying = false,
  ...rest
}: DvrTileProps) => {
  const intl = useIntl();

  const getTimeString = (): TimeString => {
    const timeInfo = (getDvrTime(parseDate((timeSeconds as number) * 1000)) || {}) as ReturnType<typeof getDvrTime>;
    const { value, unit } = timeInfo ?? {};
    const msg = messages[unit as keyof typeof messages];
    const msgLong = messages[`${unit}Long` as keyof typeof messages];
    if (msg) {
      return {
        display: `${value}${intl.formatMessage(msg)}`,
        tts: intl.formatMessage(msgLong, { value }),
      };
    }
    return {};
  };

  const [timeString, setTimeString] = useState<TimeString>(getTimeString());

  useInterval(() => {
    setTimeString(getTimeString());
  }, 60000);

  const renderMetadata = () => {
    return (
      <div
        className={classNames(
          'h-100 w-100',
          'd-flex flex-column',
          'align-items-start justify-content-between',
          'text-left',
          styles.wrapper,
        )}
      >
        <div
          className={classNames('text-wrap', 'overflow-hidden', styles.title)}
          data-testid="metadata"
        >
          {title}
        </div>
        <div
          className={classNames(
            'd-flex flex-row',
            'justify-content-center',
            styles.time,
          )}
          data-testid="dvr-time"
        >
          {timeString.display}
        </div>
      </div>
    );
  };

  const tts = [
    isNowPlaying ? intl.formatMessage(messages.nowPlaying) : null,
    title,
    timeString.tts,
  ]
    .filter(item => item)
    .join(', ');

  return (
    <MediaTile
      {...rest}
      src={src || dvrBackgroundSrc}
      autoSizeSrc={Boolean(src)}
      renderMetadata={renderMetadata}
      size={size || 'sm'}
      isNowPlaying={isNowPlaying}
      ttsText={tts}
      isDvr
    />
  );
};

export default React.memo(DvrTile);
